import React from 'react';

import { useTranslation } from '~/app';
import { CHAT_EVENT_TYPES, ChatDisplayItem } from '~/app/selectors';
import { EventType } from '~/server/src/dtos';
import { cn } from '~/utils/styles';

interface ChatEventMessageProps {
  item: ChatDisplayItem & { type: 'event' };
  players: Array<{ userId?: string; name?: string }>;
}

const ChatEventMessage: React.FC<ChatEventMessageProps> = ({ item, players }) => {
  const { t } = useTranslation();

  if (item.type !== 'event' || !item.eventType) {
    return null;
  }

  const getEventText = (): string => {
    const payload = item.payload as
      | { senderName?: string; createdBy?: string; round?: number }
      | undefined;

    const senderPlayer = players.find((p) => p.userId === item.senderId);
    const senderName = senderPlayer?.name || payload?.senderName || t('history_unknown_player');

    switch (item.eventType) {
      case CHAT_EVENT_TYPES.GAME_STARTED_ROUND_1:
        return t('event_game_started_round_started');
      case CHAT_EVENT_TYPES.ROUND_ENDED_GAME_FINISHED: {
        const roundValue = payload?.round;
        const round = roundValue !== undefined ? roundValue + 1 : 1;
        return t('event_round_ended_game_ended', {
          placeholders: { round: round.toString() },
        });
      }
      case EventType.JOINGAME:
      case EventType.JOINGAMEOPP:
        return t('event_player_joined', { placeholders: { player: senderName } });
      case EventType.LEAVEGAME:
      case EventType.LEAVEGAMEOPP:
        return t('event_player_left', { placeholders: { player: senderName } });
      case EventType.STARTGAME:
        return t('event_game_started');
      case EventType.STARTROUND: {
        const roundValue = item.payload?.round as number | undefined;
        const round = roundValue !== undefined ? roundValue + 1 : 1;
        return t('event_round_started', { placeholders: { round: round.toString() } });
      }
      case EventType.ENDROUND: {
        const roundValue = item.payload?.round as number | undefined;
        const round = roundValue !== undefined ? roundValue + 1 : 1;
        return t('event_round_ended', { placeholders: { round: round.toString() } });
      }
      case EventType.ENDGAME:
        return t('event_game_ended');
      case EventType.TM_GAMECREATED: {
        const createdBy = payload?.createdBy;
        const createdByPlayer = createdBy ? players.find((p) => p.userId === createdBy) : undefined;
        const hostName =
          createdByPlayer?.name || payload?.senderName || t('history_unknown_player');
        return t('event_game_created_by_host', { placeholders: { player: hostName } });
      }
      default:
        return '';
    }
  };

  const eventText = getEventText();
  if (!eventText) {
    return null;
  }

  const timestamp = new Date(item.timestamp).toLocaleTimeString();

  const rowClasses = cn(
    'flex w-full items-center gap-2 text-slate-600 my-0 leading-tight text-[11px] @[18rem]:text-[12px]',
  );

  const textClasses = cn(
    'min-w-0 flex-1 font-medium truncate whitespace-nowrap',
    '@[18rem]:flex-[0_1_auto]',
  );

  const timeClasses = cn('shrink-0 font-medium text-slate-600');

  /** Horizontal rules only at wider container; hidden in narrow to maximize text width. */
  const leftSeparatorClasses = cn(
    'hidden shrink-0 self-center rounded-full bg-slate-400/80 @[18rem]:block',
    'h-px w-3 @[18rem]:w-4',
  );

  const rightSeparatorClasses = leftSeparatorClasses;

  const fillSeparatorClasses = cn(
    'hidden min-w-3 flex-1 self-center rounded-full bg-slate-400/80 h-px @[18rem]:block',
  );

  // System/event messages should be text-only (no inline avatar button).
  const renderEventText = (): string => eventText;

  return (
    <div className={rowClasses} title={eventText}>
      <span className={leftSeparatorClasses} aria-hidden="true" />
      <span className={textClasses}>{renderEventText()}</span>
      <span className={fillSeparatorClasses} aria-hidden="true" />
      <span className={timeClasses}>{timestamp}</span>
      <span className={rightSeparatorClasses} aria-hidden="true" />
    </div>
  );
};

export default ChatEventMessage;
